import {
  Button,
  Divider,
  HStack,
  IconButton,
  IconName,
  IndicatorBadge,
  InlineFormattedNumber,
  MixpanelEventSource,
  ReconMismatchStatus,
  ReconResolutionEnumLabels,
  Stepper,
  Text,
  VStack,
  formattedDateWithMilliseconds,
  useJsonModal,
  useMarketAccountsContext,
  type ReconMismatch,
} from '@talos/kyoko';
import { useMemo, type ReactNode } from 'react';
import { useResolveReconMismatchesDrawer } from '../../../../hooks/useResolveReconMismatchesDrawer';

interface ReconHeaderProps {
  reconMismatches: ReconMismatch[];
  selectedReconMismatch: ReconMismatch;

  selectedIndex: number;
  onSelectedIndexChange: (newIndex: number) => void;
}

export const ReconHeader = ({
  reconMismatches,
  selectedReconMismatch,
  selectedIndex,
  onSelectedIndexChange,
}: ReconHeaderProps) => {
  const { marketAccountsByName } = useMarketAccountsContext();
  const marketAccount = selectedReconMismatch?.MarketAccount
    ? marketAccountsByName.get(selectedReconMismatch.MarketAccount)
    : undefined;

  const { openResolveReconMismatchesDrawer } = useResolveReconMismatchesDrawer();

  const { handleClickJson, jsonModal } = useJsonModal();
  const resolvableReconMismatches = useMemo(() => reconMismatches.filter(rm => rm.resolvable), [reconMismatches]);

  return (
    <>
      <HStack w="100%" background="colors.gray.030" p="spacing12" justifyContent="space-between">
        {/* Left bit */}
        <HStack w="100%" justifyContent="flex-start" gap="spacing12" alignItems="flex-start">
          <ReconHeaderItem
            label="Market Account"
            content={<Text color="colorTextImportant">{marketAccount?.DisplayName ?? marketAccount?.Name}</Text>}
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="Missing Delta"
            content={
              <InlineFormattedNumber
                truncate={false}
                number={selectedReconMismatch.MissingDelta}
                currency={selectedReconMismatch.Asset}
              />
            }
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="Events Delta"
            content={
              <InlineFormattedNumber
                truncate={false}
                number={selectedReconMismatch.EventsDelta}
                currency={selectedReconMismatch.Asset}
              />
            }
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem label="Status" content={<ReconMismatchStatus status={selectedReconMismatch.State} />} />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="Interval Start"
            content={
              <Text color="colorTextAttention">{formattedDateWithMilliseconds(selectedReconMismatch.StartTime)}</Text>
            }
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="Interval End"
            content={
              <Text color="colorTextAttention">{formattedDateWithMilliseconds(selectedReconMismatch.EndTime)}</Text>
            }
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="Resolution"
            content={
              <Text color="colorTextAttention">{ReconResolutionEnumLabels[selectedReconMismatch.Resolution]}</Text>
            }
          />
          <Divider orientation="vertical" />

          <ReconHeaderItem
            label="ID"
            content={
              <Text color="colorTextAttention" data-testid="recon-mismatch-header-id">
                {selectedReconMismatch.ID}
              </Text>
            }
          />
        </HStack>

        {/* Right bit */}
        <HStack gap="spacing8">
          {/* Buttons and actions related to the single selected mismatch */}
          <HStack gap="spacing8">
            <IconButton icon={IconName.Braces} onClick={() => handleClickJson(selectedReconMismatch)} />
            <Button
              disabled={!selectedReconMismatch.resolvable}
              data-testid="resolve-one"
              onClick={() =>
                openResolveReconMismatchesDrawer(
                  [selectedReconMismatch.ID],
                  MixpanelEventSource.ReconMismatchesDetailsResolveOneButton
                )
              }
            >
              Resolve
            </Button>
          </HStack>
          {reconMismatches.length > 1 && (
            <>
              <Divider orientation="vertical" />
              {/* Buttons and actions related to the set of multiple recon mismatches we can step through */}
              <Stepper index={selectedIndex} items={reconMismatches} onChange={onSelectedIndexChange} />
              <Button
                disabled={resolvableReconMismatches.length === 0}
                data-testid="resolve-all"
                onClick={() =>
                  openResolveReconMismatchesDrawer(
                    resolvableReconMismatches.map(m => m.ID),
                    MixpanelEventSource.ReconMismatchesDetailsResolveOneButton
                  )
                }
              >
                Resolve All ({resolvableReconMismatches.length})
              </Button>
            </>
          )}
        </HStack>
      </HStack>
      {jsonModal}
    </>
  );
};

interface ReconHeaderItemProps {
  label: string;
  content: ReactNode;
}

function ReconHeaderItem({ label, content }: ReconHeaderItemProps) {
  return (
    <VStack gap="spacing4" alignItems="flex-start">
      <IndicatorBadge children={label} />
      {content}
    </VStack>
  );
}
